import DOMPurify from 'dompurify';
import Cookies from 'js-cookie';

import { BaseConfig, CookiesConfig } from '@configs';
import { LoggerHelper } from '@helpers';
import { useFetchContentsClientCookies } from '@services';
import { CookiePrivacyPreference, useCookiesPreferencesStore, useSearchParamsStore } from '@store';
import { ONE_TRUST_COOKIE_NAME } from '@types';

import { useDrawer } from '../useDrawer';
import { useModal } from '../useModal';
import { helpers } from './useCookies.helper';

export const useCookiesConsent = () => {
	const modal = useModal('cookiesConsent');
	const drawer = useDrawer('cookiesPreferences');
	const { fetch: fetchContentsClientCookies } = useFetchContentsClientCookies();
	const {
		privacy,
		remarketing,
		setRemarketingAccepted,
		setPrivacyPreferences,
		setRemarketingCookie,
		setPrivacyCookie,
	} = useCookiesPreferencesStore();
	const clientId = useSearchParamsStore((store) => store.clientId);

	const remarketingAccepted = remarketing.accepted;
	const privacyPreferences = privacy.preferences;

	const setCookiesConsent = (preferences: CookiePrivacyPreference[], remarketingAccepted: boolean) => {
		const privacyCookieName = privacy.metadata?.name || '';
		const privacyCookieValue = helpers.transformCookieStateToString(privacy.metadata?.version, preferences);
		Cookies.set(privacyCookieName, privacyCookieValue, {
			expires: helpers.getCookieExpirationDate(privacy.metadata?.expirationTime || 43200),
			domain: CookiesConfig.domain,
		});

		const remarketingCookieName = remarketing.metadata?.name || '';
		const remarketingCookieValue = remarketingAccepted ? 'yes' : 'no';
		Cookies.set(remarketingCookieName, remarketingCookieValue, {
			expires: helpers.getCookieExpirationDate(remarketing.metadata?.expirationTime || 43200),
			domain: CookiesConfig.domain,
		});
	};

	const initCookiesConsentModal = async (showCookiesConsent: string): Promise<boolean> => {
		try {
			// get cookies configuration from BE
			const clientCookiesOutput = await fetchContentsClientCookies({ clientId });
			const { cookiesOut, familiesOut } = clientCookiesOutput?.data?.entityOut || {};

			// set initial cookies state
			const privacyCookie = helpers.getPrivacyCookie(cookiesOut, familiesOut);
			privacyCookie && setPrivacyCookie(privacyCookie);
			const remarketingCookie = helpers.getRemarketingCookie(cookiesOut);
			remarketingCookie && setRemarketingCookie(remarketingCookie);

			// get the cookie from browser
			const rawCookie = Cookies.get(privacyCookie?.metadata?.name || CookiesConfig.privacyRules);
			const objCookie = helpers.transformCookieStringToObject(rawCookie);

			// do NOT show modal if
			// - not configured in Sharepoint to show
			// - browser's cookie version === BE config cookie version
			if (
				showCookiesConsent !== 'true' ||
				!privacyCookie ||
				(objCookie?.version && objCookie?.version === privacyCookie.metadata.version)
			) {
				LoggerHelper.log('Cookies', 'initCookiesConsentModal > Not showing Modal to user:', {
					showCookiesConsent,
					privacyCookie,
					objCookie,
					clientCookiesOutput,
				});

				return false;
			}

			// show modal
			modal.show();

			return true;
		} catch (error) {
			LoggerHelper.log('App', 'initCookiesConsentModal > [catch]:', { error }, { overlay: { show: false } });

			return false;
		}
	};

	const handleAcceptAllCookies = () => {
		const acceptedPreferences =
			privacy.preferences?.map((p) => ({
				...p,
				accepted: true,
			})) || [];
		const acceptedRemarketing = true;

		// manage state
		setPrivacyPreferences(acceptedPreferences);
		setRemarketingAccepted(acceptedRemarketing);

		// set cookies in browser
		setCookiesConsent(acceptedPreferences, acceptedRemarketing);

		// hide modal and drawer
		modal.hide();
		drawer.hide();
	};

	const handleRejectAllCookies = () => {
		const rejectedPreferences =
			privacy.preferences?.map((p) => ({
				...p,
				accepted: p.isRequired ? true : false,
			})) || [];
		const rejectedRemarketing = false;

		// manage state
		setPrivacyPreferences(rejectedPreferences);
		setRemarketingAccepted(rejectedRemarketing);

		// set cookies in browser
		setCookiesConsent(rejectedPreferences, rejectedRemarketing);

		// hide modal and drawer
		modal.hide();
		drawer.hide();
	};

	const handleSavePreferences = (preferences: CookiePrivacyPreference[], remarketingAccepted: boolean) => {
		// set store state
		setRemarketingAccepted(remarketingAccepted);
		setPrivacyPreferences(preferences);

		// set cookies in browser
		setCookiesConsent(preferences, remarketingAccepted);

		// hide modal and drawer
		modal.hide();
		drawer.hide();
	};

	/* transforms the cookies values stored in the client browser, to a custom string, to be sent to the BE */
	const getFormattedCookiesConsent = () => {
		const oneTrustCookieConsent = getOneTrustCookieConsent();
		if (oneTrustCookieConsent) {
			return oneTrustCookieConsent;
		} else {
			const { name: privacyName } = privacy.metadata || {};
			const { name: remarketingName } = remarketing.metadata || {};

			const privacyCookie = Cookies.get(privacyName || '') || '';
			const remarketingCookie = Cookies.get(remarketingName || '') || '';
			const formattedRemarketingCookie = remarketingCookie ? `R:${remarketingCookie === 'yes' ? 'S' : 'N'}|` : '';

			// expected output example: "" or "cv=2##1:S|2:N|3:N|R:N|"
			return `${privacyCookie}${formattedRemarketingCookie}`;
		}
	};

	const getOneTrustCookieConsent = (): string | undefined => {
		let oneTrustCookie = Cookies.get(ONE_TRUST_COOKIE_NAME);

		// cSpell: disable
		if (BaseConfig.environment === 'development') {
			// When in local development mode,
			// use a dummy value for the OneTrust cookie (since it has a different domain, we cannot access it)
			oneTrustCookie =
				'isGpcEnabled=0&datestamp=Fri+Jan+31+2025+15:16:04+GMT+0000+(Western+European+Standard+Time)&version=202501.1.0&browserGpcFlag=0&isIABGlobal=false&hosts=&consentId=123&interactionCount=1&isAnonUser=1&landingPath=NotLandingPage&groups=1:1,3:0,2:0,4:0&intType=2&geolocation=;&AwaitingReconsent=false';
		}
		// cSpell: enable

		/* convert the cookie into a valid object to be read */
		const params = new URLSearchParams(oneTrustCookie);
		const oneTrustCookieObj = Object.fromEntries(params.entries());

		if (!oneTrustCookieObj) {
			return;
		}

		/* extract the values and sanitize them (preventing XSS) */
		const cookie = {
			consentId: DOMPurify.sanitize(oneTrustCookieObj.consentId || ''),
			datestamp: DOMPurify.sanitize(oneTrustCookieObj.datestamp || ''),
			groups: DOMPurify.sanitize(oneTrustCookieObj.groups || ''),
			version: DOMPurify.sanitize(oneTrustCookieObj.version || ''),
			interactionCount: DOMPurify.sanitize(oneTrustCookieObj.interactionCount || ''),
		};

		return `OneTrust-OptanonConsent##consentid=${cookie.consentId}&datestamp=${cookie.datestamp}&groups=${cookie.groups}&version=${cookie.version}&interactionCount=${cookie.interactionCount}`;
	};

	return {
		remarketingAccepted,
		privacyPreferences,
		initCookiesConsentModal,
		handleAcceptAllCookies,
		handleRejectAllCookies,
		handleSavePreferences,
		getFormattedCookiesConsent,
	};
};
